import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Navbar";
import "./AdvertiseWithUs.css";
import MyAdCampaigns from "./MyAdCampaigns";
import {
  ESTIMATE_DISCLAIMER,
  completionNote,
  createAdCampaign,
  createAdOrder,
  formatCount,
  getAdConfig,
  getMyAdCampaigns,
  loadRazorpayScript,
  submitAdCampaign,
  verifyAdPayment,
} from "../../services/adsApi";
import { RAZORPAY_BRAND } from "../../utils/razorpayBranding";

// =====================================================
// ADVERTISE WITH US  —  /advertise
//
// FLOW  submit → server security check of the website → admin review →
//       approval → payment → SERVER payment verification → scheduled/active
//
// The advertiser fills in the campaign and sees the price, the ESTIMATED
// impression range and the maximum duration of the chosen ad type, then
// submits it for review (or saves a draft). Payment opens only once an admin
// has approved it — from "My Campaigns", the advertiser's dashboard below
// the form — and always after a confirmation that repeats the estimate and
// how the campaign ends. Razorpay's "success" is only a hint: the server
// verifies the payment before anything is scheduled.
//
// Every price / range / duration shown here comes from the server's ad-type
// config. The checks in this file are for the advertiser's convenience only —
// the server re-validates every field and file.
// =====================================================

const EMPTY_FORM = {
  businessName: "",
  contactName: "",
  email: "",
  phone: "",
  adType: "text",
  title: "",
  description: "",
  ctaText: "",
  destinationUrl: "",
  categories: [],
  targetCity: "",
  startDate: "",
  acceptTerms: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "YYYY-MM-DD" in India time, `daysAhead` from today (the server's rule).
const istDay = (daysAhead = 0) =>
  new Date(Date.now() + daysAhead * 86400000 + 5.5 * 3600000).toISOString().slice(0, 10);

const newRequestId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const toMb = (bytes) => Math.round(bytes / (1024 * 1024));

function isWebsite(value) {
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && url.hostname.includes(".");
  } catch {
    return false;
  }
}

// Shown before Razorpay opens: what is being bought, that the range is an
// estimate, and how the campaign ends. Every number is the campaign's own
// server snapshot.
function PaymentConfirm({ campaign, typeLabel, onCancel, onProceed }) {
  const est = campaign.estimatedImpressions || {};
  return (
    <div className="adw-modal-backdrop">
      <div className="adw-modal" role="dialog" aria-modal="true" aria-labelledby="adw-pay-title">
        <h2 id="adw-pay-title" className="adw-card-heading">Confirm Payment</h2>
        <div className="adw-summary">
          <div className="adw-summary-type">{typeLabel || "Advertisement"} — {campaign.title}</div>
          <div className="adw-summary-price">₹{campaign.price}</div>
          <div className="adw-summary-line">
            Estimated Impressions: {formatCount(est.min)}–{formatCount(est.max)}
          </div>
          <div className="adw-summary-line">Campaign Duration: Up to {campaign.maxDurationDays} days</div>
          <p className="adw-disclaimer">{ESTIMATE_DISCLAIMER}</p>
          <p className="adw-disclaimer">{completionNote(campaign.maxDurationDays)}</p>
        </div>
        <div className="adw-modal-actions">
          <button type="button" className="adw-submit adw-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="adw-submit" onClick={onProceed}>
            Proceed to Pay ₹{campaign.price}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdvertiseWithUs() {
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState({ logo: null, image: null, video: null });
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);
  const requestIdRef = useRef(newRequestId());
  const fileInputs = useRef({});

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await getMyAdCampaigns();
      setCampaigns(data.campaigns || []);
      setSummary(data.summary || null);
    } catch {
      // The form still works; the dashboard simply stays as it was.
    }
  }, []);

  useEffect(() => {
    getAdConfig()
      .then((data) => {
        setConfig(data);
        const firstEnabled = (data.types || []).find((t) => t.enabled);
        if (firstEnabled) setForm((f) => ({ ...f, adType: firstEnabled.type }));
      })
      .catch(() => setLoadError("Advertising options could not be loaded. Please try again."));
    loadCampaigns();
    loadRazorpayScript();
  }, [loadCampaigns]);

  const types = config?.types || [];
  const selected = types.find((t) => t.type === form.adType) || null;
  const typeLabel = Object.fromEntries(types.map((t) => [t.type, t.label]));

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const setFile = (name, file) => {
    setFiles((f) => ({ ...f, [name]: file || null }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const chooseType = (type) => {
    setField("adType", type);
    // A creative that belongs to another ad type is dropped, not sent.
    setFiles((f) => ({ logo: f.logo, image: type === "image" ? f.image : null, video: type === "video" ? f.video : null }));
    ["image", "video"].forEach((k) => {
      if (k !== type && fileInputs.current[k]) fileInputs.current[k].value = "";
    });
  };

  const toggleCategory = (category) => {
    const has = form.categories.includes(category);
    setField("categories", has ? form.categories.filter((c) => c !== category) : [...form.categories, category]);
  };

  // ── client-side checks (the server repeats all of them) ──
  const validateForm = () => {
    const e = {};
    const required = {
      businessName: "Business / Brand Name",
      contactName: "Contact Name",
      email: "Email",
      phone: "Phone",
      title: "Ad Title",
      description: "Description",
      ctaText: "CTA Button Text",
      destinationUrl: "Destination Website",
    };
    for (const [k, label] of Object.entries(required)) {
      if (!String(form[k]).trim()) e[k] = `${label} is required`;
    }
    if (!e.email && !EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email address";
    if (!e.phone && !/^\+?\d{10,15}$/.test(form.phone.replace(/[\s\-().]/g, ""))) {
      e.phone = "Enter a valid phone number (10–15 digits)";
    }
    if (!e.destinationUrl && !isWebsite(form.destinationUrl.trim())) {
      e.destinationUrl = "Enter a full website address, e.g. https://example.com";
    }
    if (!e.description && form.description.trim().length < 10) e.description = "Description must be at least 10 characters";
    if (!form.categories.length) e.categories = "Select at least one category";
    if (!selected || !selected.enabled) e.adType = "Choose an available advertisement type";
    if (!form.acceptTerms) e.acceptTerms = "Please accept the Advertising Terms & Conditions";
    if (!e.destinationUrl && !/^https:\/\//i.test(form.destinationUrl.trim())) {
      e.destinationUrl = "The website must use HTTPS (https://)";
    }
    const maxStart = istDay(config?.maxStartDelayDays ?? 30);
    if (form.startDate && (form.startDate < istDay(0) || form.startDate > maxStart)) {
      e.startDate = "Choose a start date from today onwards (within the allowed window)";
    }

    const uploads = config?.uploads || {};
    const need = ["logo", ...(form.adType === "image" ? ["image"] : []), ...(form.adType === "video" ? ["video"] : [])];
    for (const field of need) {
      const file = files[field];
      const rule = uploads[field];
      const label = field[0].toUpperCase() + field.slice(1);
      if (!file) e[field] = `${label} is required`;
      else if (rule && !rule.mimes.includes(file.type)) e[field] = `${label} file type is not supported`;
      else if (rule && file.size > rule.maxBytes) e[field] = `${label} must be ${toMb(rule.maxBytes)}MB or smaller`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── payment ("Pay now" on an APPROVED campaign) ──
  // Always through the confirmation first, so the estimate and the end rules
  // are on screen before any money moves.
  const requestPayment = (campaign) => {
    setNotice(null);
    setConfirming(campaign);
  };

  const payForCampaign = async (campaign) => {
    setPayingId(campaign.id);
    setNotice(null);
    try {
      const ready = await loadRazorpayScript();
      if (!ready) {
        setNotice({ type: "error", text: "Payment could not be opened. Please check your connection and try again." });
        setPayingId(null);
        return;
      }
      const data = await createAdOrder(campaign.id);

      const checkout = new window.Razorpay({
        key: data.key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        ...RAZORPAY_BRAND,
        description: `${typeLabel[campaign.adType] || "Advertisement"} — ${campaign.title}`,
        order_id: data.order.id,
        prefill: { name: campaign.contactName, email: campaign.email, contact: campaign.phone },
        theme: { color: "#8b5cf6" },
        handler: async (response) => {
          try {
            const verified = await verifyAdPayment(campaign.id, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setNotice({
              type: "success",
              text: verified?.message || "Payment verified. Your campaign is scheduled.",
            });
          } catch (err) {
            setNotice({ type: "error", text: err?.response?.data?.message || "Payment verification failed." });
          } finally {
            setPayingId(null);
            loadCampaigns();
          }
        },
        modal: {
          ondismiss: () => {
            setPayingId(null);
            setNotice({ type: "info", text: "Payment cancelled. Nothing was charged — you can pay any time from My Campaigns." });
            loadCampaigns();
          },
        },
      });
      checkout.on("payment.failed", (response) => {
        setPayingId(null);
        setNotice({ type: "error", text: response?.error?.description || "Payment failed. Nothing was charged." });
      });
      checkout.open();
    } catch (err) {
      setPayingId(null);
      setNotice({ type: "error", text: err?.response?.data?.message || "Something went wrong while opening payment." });
      loadCampaigns();
    }
  };

  // Submit for review (default) or keep as a draft.
  const handleSubmit = async (event, { asDraft = false } = {}) => {
    event.preventDefault();
    if (submitting) return;
    setNotice(null);
    if (!validateForm()) {
      setNotice({ type: "error", text: "Please correct the highlighted fields." });
      return;
    }

    const fd = new FormData();
    ["businessName", "contactName", "email", "phone", "adType", "title", "description", "ctaText", "destinationUrl"].forEach(
      (k) => fd.append(k, form[k].trim())
    );
    form.categories.forEach((c) => fd.append("categories", c));
    fd.append("targetCity", form.targetCity || "All Cities");
    if (form.startDate) fd.append("startDate", form.startDate);
    fd.append("acceptTerms", String(form.acceptTerms));
    if (asDraft) fd.append("saveAsDraft", "true");
    fd.append("clientRequestId", requestIdRef.current);
    fd.append("logo", files.logo);
    if (form.adType === "image") fd.append("image", files.image);
    if (form.adType === "video") fd.append("video", files.video);

    setSubmitting(true);
    try {
      const data = await createAdCampaign(fd);
      // The submission is stored — a fresh id for the next one, a clean form.
      requestIdRef.current = newRequestId();
      setForm((f) => ({ ...EMPTY_FORM, adType: f.adType }));
      setFiles({ logo: null, image: null, video: null });
      Object.values(fileInputs.current).forEach((input) => input && (input.value = ""));
      setNotice({
        type: "success",
        text:
          data?.message ||
          (asDraft
            ? "Draft saved. Submit it for review from My Campaigns."
            : "Campaign submitted for review. You can pay once it is approved."),
      });
      await loadCampaigns();
    } catch (err) {
      const res = err?.response?.data;
      if (res?.field) setErrors((e) => ({ ...e, [res.field]: res.message }));
      setNotice({ type: "error", text: res?.message || "The campaign could not be created. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  // Draft → server security check → Pending Review.
  const submitDraft = async (campaign) => {
    setSubmittingId(campaign.id);
    setNotice(null);
    try {
      const data = await submitAdCampaign(campaign.id);
      setNotice({ type: "success", text: data?.message || "Campaign submitted for review." });
    } catch (err) {
      setNotice({ type: "error", text: err?.response?.data?.message || "The campaign could not be submitted." });
    } finally {
      setSubmittingId(null);
      loadCampaigns();
    }
  };

  const fieldError = (name) => (errors[name] ? <span className="adw-error">{errors[name]}</span> : null);

  const fileField = (name, label, accept, hint) => (
    <div className="adw-field">
      <label className="adw-label" htmlFor={`adw-${name}`}>{label}</label>
      <input
        id={`adw-${name}`}
        type="file"
        accept={accept}
        className="adw-file"
        ref={(el) => (fileInputs.current[name] = el)}
        onChange={(e) => setFile(name, e.target.files?.[0])}
      />
      <span className="adw-hint">{hint}</span>
      {fieldError(name)}
    </div>
  );

  const uploads = config?.uploads || {};
  const sizeHint = (field, formats) =>
    uploads[field] ? `${formats} · up to ${toMb(uploads[field].maxBytes)}MB` : formats;

  return (
    <div className="adw-page">
      <Navbar />
      <div className="adw-container">
        <div className="adw-header">
          <div className="adw-badge">📢 Advertising</div>
          <h1 className="adw-title">Advertise With Us</h1>
          <p className="adw-sub">
            Reach TheBlogSphere readers with a Text, Image or Video advertisement shown inside the blog feed.
          </p>
        </div>

        {loadError && <div className="adw-notice adw-notice-error">{loadError}</div>}

        {config && (
          <form className="adw-card" onSubmit={handleSubmit} noValidate>
            {/* ── ADVERTISEMENT TYPE + PRICING ── */}
            <h2 className="adw-card-heading">Advertisement Type</h2>
            <div className="adw-type-grid">
              {types.map((t) => (
                <button
                  type="button"
                  key={t.type}
                  className={`adw-type ${form.adType === t.type ? "is-selected" : ""}`}
                  onClick={() => chooseType(t.type)}
                  disabled={!t.enabled}
                  aria-pressed={form.adType === t.type}
                >
                  <span className="adw-type-name">{t.label}</span>
                  <span className="adw-type-price">₹{t.price}</span>
                  <span className="adw-type-meta">
                    Estimated Impressions: {formatCount(t.minImpressions)}–{formatCount(t.maxImpressions)}
                  </span>
                  <span className="adw-type-meta">Up to {t.maxDurationDays} days</span>
                  {!t.enabled && <span className="adw-type-off">Currently unavailable</span>}
                </button>
              ))}
            </div>
            {fieldError("adType")}

            {selected && (
              <div className="adw-summary" aria-live="polite">
                <div className="adw-summary-type">{selected.label}</div>
                <div className="adw-summary-price">₹{selected.price}</div>
                <div className="adw-summary-line">
                  Estimated Impressions: {formatCount(selected.minImpressions)}–{formatCount(selected.maxImpressions)}
                </div>
                <div className="adw-summary-line">Campaign Duration: Up to {selected.maxDurationDays} days</div>
                <p className="adw-disclaimer">{ESTIMATE_DISCLAIMER}</p>
                <p className="adw-disclaimer">{completionNote(selected.maxDurationDays)}</p>
              </div>
            )}

            {/* ── ADVERTISER ── */}
            <h2 className="adw-card-heading">Advertiser Details</h2>
            <div className="adw-grid">
              <div className="adw-field">
                <label className="adw-label" htmlFor="adw-businessName">Business / Brand Name</label>
                <input id="adw-businessName" className="adw-input" maxLength={80} value={form.businessName} onChange={(e) => setField("businessName", e.target.value)} />
                {fieldError("businessName")}
              </div>
              <div className="adw-field">
                <label className="adw-label" htmlFor="adw-contactName">Contact Name</label>
                <input id="adw-contactName" className="adw-input" maxLength={60} value={form.contactName} onChange={(e) => setField("contactName", e.target.value)} />
                {fieldError("contactName")}
              </div>
              <div className="adw-field">
                <label className="adw-label" htmlFor="adw-email">Email</label>
                <input id="adw-email" type="email" className="adw-input" maxLength={254} value={form.email} onChange={(e) => setField("email", e.target.value)} />
                {fieldError("email")}
              </div>
              <div className="adw-field">
                <label className="adw-label" htmlFor="adw-phone">Phone</label>
                <input id="adw-phone" type="tel" className="adw-input" maxLength={25} placeholder="+91 98765 43210" value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
                {fieldError("phone")}
              </div>
            </div>

            {/* ── CREATIVE ── */}
            <h2 className="adw-card-heading">Advertisement</h2>
            <div className="adw-field">
              <label className="adw-label" htmlFor="adw-title">Ad Title</label>
              <input id="adw-title" className="adw-input" maxLength={80} value={form.title} onChange={(e) => setField("title", e.target.value)} />
              {fieldError("title")}
            </div>
            <div className="adw-field">
              <label className="adw-label" htmlFor="adw-description">Description</label>
              <textarea id="adw-description" className="adw-input adw-textarea" rows={3} maxLength={300} value={form.description} onChange={(e) => setField("description", e.target.value)} />
              <span className="adw-hint">{form.description.length}/300</span>
              {fieldError("description")}
            </div>

            <div className="adw-grid">
              {fileField("logo", "Logo", "image/jpeg,image/png,image/webp", sizeHint("logo", "JPG, PNG or WEBP"))}
              {form.adType === "image" &&
                fileField("image", "Image", "image/jpeg,image/png,image/webp", sizeHint("image", "JPG, PNG or WEBP"))}
              {form.adType === "video" &&
                fileField("video", "Video", "video/mp4,video/webm,video/quicktime", sizeHint("video", "MP4, WEBM or MOV"))}
            </div>

            <div className="adw-grid">
              <div className="adw-field">
                <label className="adw-label" htmlFor="adw-ctaText">CTA Button Text</label>
                <input id="adw-ctaText" className="adw-input" maxLength={25} placeholder="e.g. Shop Now" value={form.ctaText} onChange={(e) => setField("ctaText", e.target.value)} />
                {fieldError("ctaText")}
              </div>
              <div className="adw-field">
                <label className="adw-label" htmlFor="adw-destinationUrl">Destination Website</label>
                <input id="adw-destinationUrl" type="url" className="adw-input" maxLength={2048} placeholder="https://example.com" value={form.destinationUrl} onChange={(e) => setField("destinationUrl", e.target.value)} />
                {fieldError("destinationUrl")}
              </div>
            </div>

            {/* ── TARGETING ── */}
            <h2 className="adw-card-heading">Targeting</h2>
            <div className="adw-field">
              <span className="adw-label">Category (select one or more)</span>
              <div className="adw-chips" role="group" aria-label="Category">
                {config.categories.map((c) => (
                  <button
                    type="button"
                    key={c}
                    className={`adw-chip ${form.categories.includes(c) ? "is-on" : ""}`}
                    aria-pressed={form.categories.includes(c)}
                    onClick={() => toggleCategory(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              {fieldError("categories")}
            </div>
            <div className="adw-field">
              <label className="adw-label" htmlFor="adw-targetCity">Target City</label>
              <select id="adw-targetCity" className="adw-input" value={form.targetCity} onChange={(e) => setField("targetCity", e.target.value)}>
                <option value="">All Cities</option>
                {config.cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {fieldError("targetCity")}
            </div>
            <div className="adw-field">
              <label className="adw-label" htmlFor="adw-startDate">Start Date (optional)</label>
              <input
                id="adw-startDate"
                type="date"
                className="adw-input"
                min={istDay(0)}
                max={istDay(config.maxStartDelayDays ?? 30)}
                value={form.startDate}
                onChange={(e) => setField("startDate", e.target.value)}
              />
              <span className="adw-hint">Leave empty to start as soon as your payment is confirmed.</span>
              {fieldError("startDate")}
            </div>

            {/* ── TERMS + SUBMIT ── */}
            <label className="adw-terms">
              <input type="checkbox" checked={form.acceptTerms} onChange={(e) => setField("acceptTerms", e.target.checked)} />
              <span>
                I have read and accept the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">Advertising Terms &amp; Conditions</Link>.
              </span>
            </label>
            {fieldError("acceptTerms")}

            {notice && <div className={`adw-notice adw-notice-${notice.type}`}>{notice.text}</div>}

            <div className="adw-actions">
              <button
                type="button"
                className="adw-submit adw-secondary"
                disabled={submitting}
                onClick={(e) => handleSubmit(e, { asDraft: true })}
              >
                Save as Draft
              </button>
              <button type="submit" className="adw-submit" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit for Review"}
              </button>
            </div>
            <p className="adw-footnote">
              Your website is security-checked and your advertisement is reviewed by our team. You pay
              {selected ? ` ₹${selected.price}` : ""} only after approval (🔒 secure Razorpay payment, verified by our server).
            </p>
          </form>
        )}

        <MyAdCampaigns
          campaigns={campaigns}
          summary={summary}
          typeLabel={typeLabel}
          payingId={payingId}
          onPay={requestPayment}
          submittingId={submittingId}
          onSubmitDraft={submitDraft}
        />
      </div>

      {confirming && (
        <PaymentConfirm
          campaign={confirming}
          typeLabel={typeLabel[confirming.adType]}
          onCancel={() => setConfirming(null)}
          onProceed={() => {
            const c = confirming;
            setConfirming(null);
            payForCampaign(c);
          }}
        />
      )}
    </div>
  );
}

export default AdvertiseWithUs;
